import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, User, Home, MapPin, Building, Mail, Download, Smartphone, Heart, Map as MapIcon2, Gift } from 'lucide-react';
import { HeartIcon, MapIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLoading } from '../../contexts/LoadingContext';
import { useGiftCart } from '../../contexts/GiftCartContext';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import ThemeSwitcher from '../Theme/ThemeSwitcher';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import UserAvatar from '../Common/UserAvatar';
import logoImage from '../../assets/logo.png';
import { getGiftShopUrl, getMainSiteUrl, isGiftShopDomain } from '../../utils/domainUtils';

const navLinks = [
  {
    name: 'Home',
    href: '/',
    icon: Home,
    subtitle: 'Start your journey'
  },
  {
    name: 'Safari Tours',
    href: '/tours',
    icon: MapPin,
    subtitle: 'Explore our packages'
  },
  {
    name: 'About Us',
    href: '/about',
    icon: User,
    subtitle: 'Learn about us'
  },
  {
    name: 'Gift Shop',
    href: '/gift-shop',
    icon: Gift,
    subtitle: 'Shop the SightTrails store'
  },
  {
    name: 'Contact',
    href: '/contact',
    icon: Mail,
    subtitle: 'Plan your safari'
  },
];

const Header: React.FC = () => {
  const { user, isAuthenticated, signOut } = useAuth();
  const { counts } = useNavigation();
  const { totalItems: giftCartCount } = useGiftCart();
  const navigate = useNavigate();
  const location = useLocation();
  const { actualTheme } = useTheme();
  const { showSplash } = useLoading();
  const { isInstallable, isInstalled, isIOS, installPWA } = usePWAInstall();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [currentSection, setCurrentSection] = useState<string>('');
  const [isInstalling, setIsInstalling] = useState(false);

  // Debounced hover handlers to prevent jittery animations
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 100); // Small delay to prevent flickering
  };

  useEffect(() => {
    const handleScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolled(window.scrollY > 20);
      }, 10);

      // Detect current section based on route
      const path = location.pathname;
      if (path === '/') {
        setCurrentSection('Home');
      } else if (path === '/tours') {
        setCurrentSection('Safari Tours');
      } else if (path === '/about') {
        setCurrentSection('About Us');
      } else if (path === '/contact') {
        setCurrentSection('Contact');
      } else if (path === '/gift-shop') {
        setCurrentSection('Gift Shop');
      } else if (path === '/categories') {
        setCurrentSection('Categories');
      } else if (path.startsWith('/category/')) {
        const categorySlug = path.split('/category/')[1];
        const categoryName = categorySlug
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        setCurrentSection(categoryName);
      } else if (path.startsWith('/tours/')) {
        setCurrentSection('Tour Details');
      } else {
        setCurrentSection('');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [location.pathname]);

  const handleHomeClick = (e: React.MouseEvent) => {
    // Trigger splash for home transition
    showSplash();
  };

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const handleInstallApp = async () => {
    console.log('Install button clicked'); // Debug log
    setIsInstalling(true);
    try {
      const success = await installPWA();
      console.log('Install result:', success); // Debug log
      if (success) {
        // Close the sidebar after successful installation
        setTimeout(() => {
          setIsMobileMenuOpen(false);
        }, 1000);
      }
    } catch (error) {
      console.error('Error installing app:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    
    if (href === '/gift-shop') {
      if (!isGiftShopDomain()) {
        window.open(getGiftShopUrl(), '_blank');
        return;
      }
    } else if (isGiftShopDomain()) {
      window.location.href = getMainSiteUrl(href);
      return;
    }
    
    // Use replace: true when going to home to prevent back-button loops 
    // as requested (Home becomes the new root in the history stack)
    navigate(href, { replace: href === '/' });
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{
          y: 0,
          opacity: isMobileMenuOpen ? 0 : 1, // Hide when sidebar is open
          scale: isMobileMenuOpen ? 0.8 : 1, // Shrink when hiding
        }}
        transition={{
          duration: isMobileMenuOpen ? 0.3 : 0.6, // Faster hide, slower dramatic reveal
          ease: isMobileMenuOpen
            ? [0.4, 0, 1, 1] // Quick ease out when hiding
            : [0.175, 0.885, 0.32, 1.275], // Dramatic bounce back when showing
          type: 'tween'
        }}
        className="fixed top-4 left-0 right-0 z-[9999] w-full flex justify-center px-4 sm:px-6"
        style={{
          isolation: 'isolate',
          transform: 'translate3d(0, 0, 0)',
          willChange: 'transform, opacity, scale',
          pointerEvents: isMobileMenuOpen ? 'none' : 'auto' // Disable interactions when hidden
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <motion.nav
          animate={{
            width: isScrolled
              ? (isHovered ? '100%' : '220px') // Reduced from 280px to 220px
              : '100%',
            maxWidth: isScrolled
              ? (isHovered ? '1200px' : '220px')
              : '1200px',
          }}
          transition={{
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1],
            type: 'tween'
          }}
          className={`
            flex items-center
            ${isScrolled && !isHovered ? 'justify-between' : 'justify-between'}
            h-12
            px-1 xs:px-2 sm:px-4
            ${actualTheme === 'dark'
              ? 'bg-slate-900/95 backdrop-blur-xl border-slate-700/50'
              : 'bg-white/95 backdrop-blur-md border-gray-200/50'
            }
            rounded-full border
            ${actualTheme === 'dark'
              ? 'shadow-2xl shadow-black/40'
              : 'shadow-lg hover:shadow-xl'
            }
            overflow-hidden
            mx-auto
            w-full
            max-w-[98vw] xs:max-w-[95vw] sm:max-w-[90vw] lg:max-w-[1200px]
            transition-all duration-300
          `}
          style={{
            minHeight: '48px', // Ensure consistent height
            alignItems: 'center', // Force vertical centering
            ...(actualTheme === 'dark' && {
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)'
            })
          }}
        >
          {/* Logo & Brand Group */}
          <div className="flex items-center flex-shrink-0 group cursor-pointer z-10" onClick={() => { handleHomeClick({} as any); handleNavClick('/'); }}>
          {/* Logo - Fixed vertical positioning with explicit centering */}
          <motion.button
            onClick={() => {
              handleHomeClick({} as any);
              handleNavClick('/');
            }}
            className="flex items-center justify-center group flex-shrink-0"
            animate={{
              scale: isScrolled && !isHovered ? 1 : 1,
            }}
            transition={{
              duration: 0.3,
              ease: [0.4, 0, 0.2, 1]
            }}
            style={{
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div
              className="flex items-center justify-center"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '40px',
                position: 'relative',
                top: '-2px', // Just a bit higher
                overflow: 'hidden',
                ...(actualTheme === 'dark' && {
                  backgroundColor: '#ffffff',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  top: '0px' // Keep circle in normal position
                })
              }}
            >
              <motion.img
                src={logoImage}
                alt="SightTrails Safaris"
                className="object-contain w-16 h-16 sm:w-18 sm:h-18"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.2 }}
                style={{
                  display: 'block',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  ...(actualTheme === 'dark' && {
                    width: '48px',
                    height: '48px'
                  })
                }}
              />
            </div>
          </motion.button>

          {/* Brand Name / Section - Right next to logo with responsive spacing */}
          <div className="flex items-center -ml-4 xs:-ml-3 sm:-ml-2">
            {isScrolled && !isHovered ? (
              currentSection && (
                <motion.span
                  key={currentSection}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`text-sm font-medium ${actualTheme === 'dark' ? 'text-slate-100' : 'text-gray-900'
                    }`}
                  style={{
                    height: '48px',
                    alignItems: 'center',
                    lineHeight: '1',
                    display: 'flex'
                  }}
                >
                  {currentSection}
                </motion.span>
              )
            ) : (
              <motion.div
                className="flex items-center flex-shrink-0"
                transition={{ duration: 0.3 }}
                style={{
                  height: '48px',
                  alignItems: 'center'
                }}
              >
                <span className={`font-bold text-sm xs:text-base sm:text-lg ${actualTheme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                  }`} style={{ lineHeight: '1' }}>
                  SightTrails
                </span>
                <span className={`font-bold text-sm xs:text-base sm:text-lg ml-0.5 ${actualTheme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`} style={{ lineHeight: '1' }}>
                  Safaris
                </span>
              </motion.div>
            )}
          </div>
          </div>

          {/* Desktop Navigation - visible on md+ screens */}
          <motion.div
            className="hidden lg:flex items-center gap-2 lg:gap-3 xl:gap-4 2xl:gap-6 flex-1 justify-center min-w-0 px-1 lg:px-2"
            animate={{
              opacity: isScrolled && !isHovered ? 0 : 1,
            }}
            transition={{ duration: 0.3 }}
          >
            {navLinks.map((link) => (
              <motion.button
                key={link.name}
                onClick={() => handleNavClick(link.href)}
                className={`text-xs xl:text-sm tracking-tight xl:tracking-normal font-medium transition-colors whitespace-nowrap flex-shrink-0 relative flex items-center gap-1 ${actualTheme === 'dark'
                  ? 'text-slate-300 hover:text-slate-100'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                {link.name}
                {link.name === 'Gift Shop' && giftCartCount > 0 && (
                  <span className="flex h-3.5 min-w-[14px] xl:h-4 xl:min-w-[16px] px-1 items-center justify-center rounded-full bg-blue-600 text-[9px] xl:text-[10px] font-bold text-white shadow-sm animate-in zoom-in duration-300">
                    {giftCartCount}
                  </span>
                )}
              </motion.button>
            ))}
          </motion.div>
          {/* Desktop CTA - visible on lg+ screens */}
          <motion.div
            className="hidden lg:flex items-center gap-1 xl:gap-1.5 2xl:gap-2 flex-shrink-0 z-10 mr-1 xl:mr-0"
            animate={{
              opacity: isScrolled && !isHovered ? 0 : 1,
            }}
            transition={{ duration: 0.3 }}
          >
            {/* Desktop CTA - no Install App button here anymore */}
            {isAuthenticated && user ? (
              <>
                <a href={isGiftShopDomain() ? getMainSiteUrl('/my-safaris') : "/my-safaris"} className="relative">
                  <button className={`text-[11px] xl:text-sm px-2 xl:px-3 py-1.5 rounded-lg transition-colors tracking-tight xl:tracking-normal font-medium border ${actualTheme === 'dark'
                    ? 'text-sky-300 bg-sky-900/20 border-sky-800/30 hover:bg-sky-800/50 hover:border-sky-500/50'
                    : 'text-sky-700 bg-sky-50 border-sky-100 hover:bg-sky-100 hover:border-sky-200'
                    }`}>
                    My Safaris
                  </button>
                  {counts.savedSafaris > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5 xl:h-4 xl:w-4 items-center justify-center rounded-full bg-red-600 text-[9px] xl:text-[10px] font-bold text-white shadow-sm animate-in zoom-in duration-300">
                      {counts.savedSafaris}
                    </span>
                  )}
                </a>
                <a href={isGiftShopDomain() ? getMainSiteUrl('/wishlist') : "/wishlist"} className="relative">
                  <button className={`text-[11px] xl:text-sm px-2 xl:px-3 py-1.5 rounded-lg transition-colors tracking-tight xl:tracking-normal font-medium border ${actualTheme === 'dark'
                    ? 'text-sky-300 bg-sky-900/20 border-sky-800/30 hover:bg-sky-800/50 hover:border-sky-500/50'
                    : 'text-sky-700 bg-sky-50 border-sky-100 hover:bg-sky-100 hover:border-sky-200'
                    }`}>
                    Wishlist
                  </button>
                  {counts.wishlist > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5 xl:h-4 xl:w-4 items-center justify-center rounded-full bg-red-600 text-[9px] xl:text-[10px] font-bold text-white shadow-sm animate-in zoom-in duration-300">
                      {counts.wishlist}
                    </span>
                  )}
                </a>
                <a href={isGiftShopDomain() ? getMainSiteUrl('/my-bookings') : "/my-bookings"} className="relative">
                  <button className={`text-[11px] xl:text-sm px-2 xl:px-3 py-1.5 rounded-lg transition-colors tracking-tight xl:tracking-normal font-medium border ${actualTheme === 'dark'
                    ? 'text-sky-300 bg-sky-900/20 border-sky-800/30 hover:bg-sky-800/50 hover:border-sky-500/50'
                    : 'text-sky-700 bg-sky-50 border-sky-100 hover:bg-sky-100 hover:border-sky-200'
                    }`}>
                    My Bookings
                  </button>
                  {counts.myBookings > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5 xl:h-4 xl:w-4 items-center justify-center rounded-full bg-emerald-600 text-[9px] xl:text-[10px] font-bold text-white shadow-sm animate-in zoom-in duration-300">
                      {counts.myBookings}
                    </span>
                  )}
                </a>
                <a href={isGiftShopDomain() ? getMainSiteUrl('/profile') : "/profile"} className="flex items-center gap-1 xl:gap-2 ml-1">
                  <UserAvatar name={user.name} avatarUrl={user.picture} className="w-6 h-6 xl:w-7 xl:h-7 hidden lg:flex" />
                  <button className={`text-[11px] xl:text-sm px-2 xl:px-3 py-1.5 rounded-lg transition-colors tracking-tight xl:tracking-normal ${actualTheme === 'dark'
                    ? 'text-gray-300 hover:text-white hover:bg-gray-800'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}>
                    Profile
                  </button>
                </a>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    signOut();
                  }}
                  className={`text-[11px] xl:text-sm px-2 xl:px-3 py-1.5 rounded-lg transition-colors tracking-tight xl:tracking-normal ${actualTheme === 'dark'
                    ? 'text-gray-300 hover:text-red-400 hover:bg-gray-800'
                    : 'text-gray-600 hover:text-red-600 hover:bg-gray-100'
                    }`}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <a href={isGiftShopDomain() ? getMainSiteUrl('/login') : "/login"}>
                  <button className={`text-[11px] xl:text-sm px-3 py-1.5 rounded-lg transition-colors ${actualTheme === 'dark'
                    ? 'text-gray-300 hover:text-white hover:bg-gray-800'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}>
                    Login
                  </button>
                </a>
                <a href={isGiftShopDomain() ? getMainSiteUrl('/tours') : "/tours"}>
                  <button className="text-[11px] xl:text-sm px-3 xl:px-4 py-1.5 rounded-lg font-medium transition-colors bg-blue-600 hover:bg-blue-700 text-white">
                    Plan Safari
                  </button>
                </a>
              </>
            )}
          </motion.div>

          {/* Mobile Spacer - Only on mobile to push hamburger to right */}
          <div className="flex-1 lg:hidden min-w-0"></div>


          {/* Mobile Menu - hamburger on small screens only */}
          <motion.div
            className="lg:hidden flex items-center gap-1 xs:gap-2 flex-shrink-0 z-10"
            animate={{
              opacity: isScrolled && !isHovered ? 1 : 1, // Always visible on mobile
            }}
            transition={{ duration: 0.3 }}
            style={{
              height: '48px', // Match navbar height
              alignItems: 'center'
            }}
          >
            {/* Theme Switcher - hide when shrunk on very small screens */}
            <motion.div
              animate={{
                width: isScrolled && !isHovered ? 0 : 'auto',
                opacity: isScrolled && !isHovered ? 0 : 1,
              }}
              transition={{ duration: 0.3 }}
              className="hidden xs:flex"
              style={{ overflow: 'hidden', height: '48px', alignItems: 'center', display: 'flex' }}
            >
              <ThemeSwitcher />
            </motion.div>

            {/* Hamburger Menu - always visible */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className={`p-1.5 xs:p-2 rounded-lg transition-colors flex items-center justify-center ${actualTheme === 'dark'
                ? 'text-gray-300 hover:text-white hover:bg-gray-800'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              style={{
                height: '36px',
                width: '36px',
                minHeight: '36px',
                minWidth: '36px'
              }}
            >
              <Menu size={18} />
            </button>
          </motion.div>

          {/* Desktop Theme Switcher */}
          <motion.div
            className="hidden xl:flex items-center ml-3"
            animate={{
              opacity: isScrolled && !isHovered ? 1 : 1, // Always visible on desktop
            }}
            transition={{ duration: 0.3 }}
          >
            <ThemeSwitcher />
          </motion.div>
        </motion.nav>
      </motion.header>

      {/* Professional Sidebar Menu - Clean Minimal Design */}
      <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>

        <SheetContent
          side="right"
          className={`w-[75vw] sm:w-[320px] p-0 overflow-hidden !rounded-[2.5rem] !h-[calc(100vh-2rem)] !top-4 !bottom-4 !right-4 !left-auto border border-white/20 transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-900/95 backdrop-blur-2xl shadow-[0_30px_60px_-15px_rgba(0,0,0,0.6)]' : 'bg-white/95 backdrop-blur-3xl shadow-[0_30px_60px_-15px_rgba(0,0,0,0.2)]'
            } [&>button]:hidden`}
        >
          <div className="absolute top-6 right-6 z-[100]">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsMobileMenuOpen(false);
              }}
              className={`p-2.5 rounded-full shadow-md transition-transform hover:scale-105 active:scale-95 flex items-center justify-center ${
                actualTheme === 'dark'
                  ? 'bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
              aria-label="Close menu"
            >
              <X size={20} strokeWidth={2.5} />
            </button>
          </div>

          <div className="flex flex-col h-full relative z-10">
            <SheetTitle className="sr-only">Mobile Menu</SheetTitle>
            <SheetDescription className="sr-only">Main site navigation options</SheetDescription>
            {/* Clean Header */}
            <SheetHeader className={`px-6 pt-8 pb-6 border-b ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
              }`}>
              <div className="flex items-center pr-12">
                <div className="flex items-center gap-3">
                  <img
                    src={logoImage}
                    alt="SightTrails Safaris"
                    className="w-10 h-10 object-contain flex-shrink-0"
                  />
                  <div className="flex items-center">
                    <SheetTitle className={`text-xl font-bold ${actualTheme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                      SightTrails
                    </SheetTitle>
                    <SheetTitle className={`text-xl font-bold ml-1 ${actualTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Safaris
                    </SheetTitle>
                  </div>
                </div>
              </div>
            </SheetHeader>

            {/* Clean Navigation Links */}
            <div className="flex-1 overflow-y-auto px-4 py-6">
              <nav className="space-y-1">
                {navLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <button
                      key={link.name}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleNavClick(link.href);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left group ${actualTheme === 'dark'
                        ? 'hover:bg-slate-800'
                        : 'hover:bg-neutral-100'
                        }`}
                    >
                      <Icon size={20} className={`transition-colors ${actualTheme === 'dark'
                        ? 'text-slate-400 group-hover:text-slate-200'
                        : 'text-neutral-500 group-hover:text-neutral-900'
                        }`} />
                      <div className="flex-1">
                        <div className={`font-medium text-sm flex items-center gap-2 ${actualTheme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                          {link.name}
                          {link.name === 'Gift Shop' && giftCartCount > 0 && (
                            <span className="flex h-4 min-w-[16px] px-1 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white shadow-sm">
                              {giftCartCount}
                            </span>
                          )}
                        </div>
                        <div className={`text-xs ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'
                          }`}>
                          {link.subtitle}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </nav>

              {/* User Actions Section */}
              <div className={`mt-6 pt-6 border-t ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
                }`}>
                <div className="space-y-1">
                  {isAuthenticated && user ? (
                    <>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsMobileMenuOpen(false);
                          navigate('/wishlist');
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left relative ${actualTheme === 'dark'
                          ? 'hover:bg-slate-800'
                          : 'hover:bg-neutral-100'
                          }`}
                      >
                        <HeartIcon className={`w-5 h-5 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'}`} />
                        <div className="flex-1">
                          <div className={`font-medium text-sm ${actualTheme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                            Wishlist
                          </div>
                          <div className={`text-xs ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'
                            }`}>
                            Tours you've hearted
                          </div>
                        </div>
                        {counts.wishlist > 0 && (
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white shadow-sm">
                            {counts.wishlist}
                          </span>
                        )}
                      </button>

                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsMobileMenuOpen(false);
                          navigate('/my-safaris');
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left relative ${actualTheme === 'dark'
                          ? 'hover:bg-slate-800'
                          : 'hover:bg-neutral-100'
                          }`}
                      >
                        <MapIcon className={`w-5 h-5 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'}`} />
                        <div className="flex-1">
                          <div className={`font-medium text-sm ${actualTheme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                            My Safaris
                          </div>
                          <div className={`text-xs ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'
                            }`}>
                            Your custom itineraries
                          </div>
                        </div>
                        {counts.savedSafaris > 0 && (
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white shadow-sm">
                            {counts.savedSafaris}
                          </span>
                        )}
                      </button>

                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsMobileMenuOpen(false);
                          navigate('/my-bookings');
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left relative ${actualTheme === 'dark'
                          ? 'hover:bg-slate-800'
                          : 'hover:bg-neutral-100'
                          }`}
                      >
                        <Building size={20} className={actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'} />
                        <div className="flex-1">
                          <div className={`font-medium text-sm ${actualTheme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                            My Bookings
                          </div>
                          <div className={`text-xs ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'
                            }`}>
                            View your reservations
                          </div>
                        </div>
                        {counts.myBookings > 0 && (
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white shadow-sm">
                            {counts.myBookings}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          signOut();
                          setIsMobileMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left ${actualTheme === 'dark'
                          ? 'hover:bg-red-900/20 hover:text-red-400'
                          : 'hover:bg-red-50 hover:text-red-600'
                          }`}
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className={actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'}>
                          <path d="M7 17L2 12M2 12L7 7M2 12H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="flex-1">
                          <div className={`font-medium text-sm ${actualTheme === 'dark' ? 'text-red-400' : 'text-red-600'
                            }`}>
                            Logout
                          </div>
                          <div className={`text-xs ${actualTheme === 'dark' ? 'text-red-500/70' : 'text-red-500/70'
                            }`}>
                            Sign out of your account
                          </div>
                        </div>
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsMobileMenuOpen(false);
                        navigate('/login');
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left ${actualTheme === 'dark'
                        ? 'hover:bg-slate-800'
                        : 'hover:bg-neutral-100'
                        }`}
                    >
                      <User size={20} className={actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'} />
                      <div className="flex-1">
                        <div className={`font-medium text-sm ${actualTheme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                          Login
                        </div>
                        <div className={`text-xs ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'
                          }`}>
                          Access your account
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              </div>

              {/* Bottom Section - Action Button */}
              <div className={`px-4 pb-6 pt-4 border-t mt-auto ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
                }`}>
                {!isAuthenticated && (
                  <Link
                    to="/tours"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block w-full text-center px-6 py-4 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] mb-3 ${actualTheme === 'dark'
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                  >
                    Plan Your Safari
                  </Link>
                )}

                {/* PWA Install Button - Blue color like "Safaris" */}
                {isInstallable && !isInstalled && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleInstallApp();
                    }}
                    disabled={isInstalling}
                    className={`block w-full text-center px-6 py-4 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] ${actualTheme === 'dark'
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                      } ${isInstalling ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {isInstalling ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                        Installing...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        {isIOS ? (
                          <Smartphone className="h-5 w-5 mr-2" />
                        ) : (
                          <Download className="h-5 w-5 mr-2" />
                        )}
                        Install App
                      </div>
                    )}
                  </button>
                )}

                {/* App Already Installed Message */}
                {isInstalled && (
                  <div className={`text-center px-6 py-4 rounded-lg ${actualTheme === 'dark'
                    ? 'bg-green-900/20 text-green-300'
                    : 'bg-green-50 text-green-700'
                    }`}>
                    <div className="flex items-center justify-center">
                      <Smartphone className="h-5 w-5 mr-2" />
                      App Installed!
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default Header;
